import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { CONTACT } from "@/lib/contact";

export function MarketingNav() {
    const { t } = useI18n();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const links = [
        { to: "/features", label: t("nav.features") },
        { to: "/pricing", label: t("nav.pricing") },
        { to: "/about", label: t("nav.about") },
        { to: "/faq", label: t("nav.faq") },
        { to: "/contact", label: t("nav.contact") },
    ];
    return (
        <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
                <Logo />
                <nav className="hidden items-center gap-8 md:flex">
                    {links.map((l) => (
                        <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                            className="text-sm font-medium text-muted-foreground transition-colors duration-200 hover:text-foreground">
                            {l.label}
                        </Link>
                    ))}
                </nav>
                <div className="hidden items-center gap-3 md:flex">
                    <LanguageSwitcher compact />
                    <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn" className="rounded-full">{t("nav.login")}</Button></Link>
                    <Button onClick={() => nav("/register")} data-testid="nav-trial-btn" className="rounded-full">{t("common.startTrial")}</Button>
                </div>
                <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
                    {open ? <X /> : <Menu />}
                </button>
            </div>
            {open && (
                <div className="border-t border-border/60 bg-background px-6 py-4 md:hidden">
                    <div className="flex flex-col gap-4">
                        {links.map((l) => <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm font-medium">{l.label}</Link>)}
                        <div className="flex items-center gap-3 pt-2">
                            <LanguageSwitcher compact />
                            <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full">{t("nav.login")}</Button></Link>
                        </div>
                        <Button onClick={() => nav("/register")} className="w-full rounded-full">{t("common.startTrial")}</Button>
                    </div>
                </div>
            )}
        </header>
    );
}

export function MarketingFooter() {
    const { t } = useI18n();
    return (
        <footer className="border-t border-border/60 bg-primary text-primary-foreground">
            <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 md:grid-cols-4">
                <div className="md:col-span-2">
                    <Logo dark />
                    <p className="mt-4 max-w-sm text-sm text-primary-foreground/70">{t("marketing.tagline")}</p>
                    <ul className="mt-6 space-y-2 text-sm text-primary-foreground/80" data-testid="footer-contact">
                        <li><a href={`mailto:${CONTACT.email}`} className="hover:text-accent">{CONTACT.email}</a></li>
                        <li><a href={CONTACT.phoneHref} className="hover:text-accent">{CONTACT.phone}</a></li>
                        <li>{CONTACT.address}</li>
                    </ul>
                </div>
                <div>
                    <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-primary-foreground/60">Product</h4>
                    <ul className="space-y-2 text-sm text-primary-foreground/80">
                        <li><Link to="/features" className="hover:text-accent">{t("nav.features")}</Link></li>
                        <li><Link to="/pricing" className="hover:text-accent">{t("nav.pricing")}</Link></li>
                        <li><Link to="/faq" className="hover:text-accent">{t("nav.faq")}</Link></li>
                    </ul>
                </div>
                <div>
                    <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-primary-foreground/60">Legal</h4>
                    <ul className="space-y-2 text-sm text-primary-foreground/80">
                        <li><Link to="/legal/privacy" className="hover:text-accent">Privacy Policy</Link></li>
                        <li><Link to="/legal/terms" className="hover:text-accent">Terms & Conditions</Link></li>
                        <li><Link to="/legal/cookie" className="hover:text-accent">Cookie Policy</Link></li>
                        <li><Link to="/contact" className="hover:text-accent">{t("nav.contact")}</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-primary-foreground/10 px-6 py-6 text-center text-xs text-primary-foreground/50">
                © {new Date().getFullYear()} CRN Agenda Planner — {t("marketing.tagline")}
            </div>
        </footer>
    );
}

export default function MarketingShell({ children }) {
    return (
        <div className="min-h-screen bg-background">
            <MarketingNav />
            <main>{children}</main>
            <MarketingFooter />
        </div>
    );
}
